/**
 * Turns a keyboard event into the key names the rest of this directory speaks:
 * react-hotkeys-hook's syntax, which the registry's defaults are written in and
 * the matcher compares against.
 *
 * Names come from `event.code`, not `event.key`, so a shifted glyph records as
 * the key under it: `?` is `shift+slash`, exactly how `system.help` spells it.
 * react-hotkeys-hook matches a chord against either the code-derived name or
 * the typed key, so a name made here always matches the key that made it.
 */

import { isMac } from "./platform.ts";

/** Modifier tokens in the order they are written in a chord. */
const MODIFIER_ORDER = ["mod", "ctrl", "meta", "alt", "shift"] as const;

const MODIFIER_CODES = new Set([
  "ShiftLeft",
  "ShiftRight",
  "ControlLeft",
  "ControlRight",
  "AltLeft",
  "AltRight",
  "MetaLeft",
  "MetaRight",
  "OSLeft",
  "OSRight",
]);

/**
 * The hotkey name for the physical key an event reports, or `null` for a lone
 * modifier. Letters, digits and arrows drop their `Key`/`Digit`/`Arrow` prefix
 * the way react-hotkeys-hook's own `mapKey` does; everything else is the code
 * lowercased (`Slash` → `slash`, `BracketLeft` → `bracketleft`).
 */
export const keyNameFromEvent = (event: KeyboardEvent): string | null => {
  const { code } = event;
  if (!code || MODIFIER_CODES.has(code)) return null;
  const stripped = code.replace(/^(Key|Digit|Numpad|Arrow)/, "");
  return stripped.toLowerCase();
};

/**
 * The chord an event represents (`mod+shift+k`, `alt+1`, `slash`), or `null`
 * for a lone modifier. The platform's primary modifier (⌘ on a Mac, Ctrl
 * elsewhere) is written as `mod`, matching the registry, so a binding recorded
 * on one platform means the equivalent chord on the other; any other modifier
 * is written literally.
 */
export const comboFromEvent = (event: KeyboardEvent, mac: boolean = isMac()): string | null => {
  const key = keyNameFromEvent(event);
  if (key === null) return null;
  const modifiers = new Set<string>();
  if (mac ? event.metaKey : event.ctrlKey) modifiers.add("mod");
  if (mac ? event.ctrlKey : event.metaKey) modifiers.add(mac ? "ctrl" : "meta");
  if (event.altKey) modifiers.add("alt");
  if (event.shiftKey) modifiers.add("shift");
  const ordered = MODIFIER_ORDER.filter((m) => modifiers.has(m));
  return [...ordered, key].join("+");
};

const CANONICAL_MODIFIERS: readonly string[] = ["ctrl", "meta", "alt", "shift"];
const MODIFIER_ALIASES: Record<string, string> = { control: "ctrl", option: "alt" };

/**
 * One chord step in the form two equal chords always share: `mod` resolved to
 * this platform's modifier, aliases folded, modifiers in a fixed order. Two
 * spellings that fire on the same keystroke (`mod+n` and `ctrl+n` on Windows)
 * compare equal only after this.
 */
const resolveModifierToken = (token: string, mac: boolean) => {
  if (token === "mod") return mac ? "meta" : "ctrl";
  return MODIFIER_ALIASES[token] ?? token;
};

const normalizeChord = (chord: string, mac: boolean): string => {
  const tokens = chord
    .toLowerCase()
    .split("+")
    .map((token) => resolveModifierToken(token, mac));
  const modifiers = CANONICAL_MODIFIERS.filter((m) => tokens.includes(m));
  const keys = tokens.filter((t) => !CANONICAL_MODIFIERS.includes(t));
  return [...modifiers, ...keys].join("+");
};

/** A combo in comparable form: each step normalized (see `normalizeChord`). */
export const normalizeCombo = (combo: string, mac: boolean = isMac()): string =>
  sequenceSteps(combo)
    .map((step) => normalizeChord(step, mac))
    .join(" ");

/** True when the combo holds a modifier that changes gating: ⌘/Ctrl/Alt, not Shift. */
export const hasRealModifier = (combo: string): boolean =>
  /(^|\+)(mod|meta|ctrl|control|alt|option)(\+|$)/i.test(combo);

/** True for a literal Ctrl chord, as opposed to a `mod` that may resolve to Ctrl. */
export const isCtrlChord = (combo: string): boolean => /(^|\+)(ctrl|control)(\+|$)/i.test(combo);

/** True for a leader sequence (`g c`): bare keys pressed one after another. */
export const isSequence = (combo: string): boolean => combo.includes(" ");

/** The steps of a sequence, or the single chord as a one-step list. */
export const sequenceSteps = (combo: string): string[] => combo.split(/\s+/).filter(Boolean);
